import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/subscription");
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUserData();
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="subscription-success-page">
      <div className="text-center max-w-md mx-auto p-8">
        {status === "loading" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Vérification du paiement...
            </h1>
            <p className="text-slate-500">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Paiement réussi !
            </h1>
            <p className="text-slate-500 mb-6">
              Votre abonnement est maintenant actif. Profitez de toutes les fonctionnalités de ResponsIA.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="go-to-dashboard">
                Accéder au tableau de bord
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {status === "expired" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Session expirée
            </h1>
            <p className="text-slate-500 mb-6">
              La session de paiement a expiré. Veuillez réessayer.
            </p>
            <Link to="/subscription">
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800">
                Retour aux abonnements
              </Button>
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Vérification en cours...
            </h1>
            <p className="text-slate-500 mb-6">
              La vérification prend plus de temps que prévu. Vous recevrez une confirmation par email.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800">
                Retour au tableau de bord
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
